import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Card, CardContent, Grid, Alert, CircularProgress,
} from '@mui/material';
import { authService } from '../services/auth';
import { isValidEmail } from '../utils/validators';
import { errorMessage } from '../services/api';
import SiteFooter from '../components/SiteFooter';

const NOTES = [
  'Someone starts a group and adds everyone by email.',
  'You sign in with that same email. No password.',
  'After the draw, this page shows your person, and gift ideas other people left for them.',
  'Texts, if you want them, only contain a link. Never a name.',
];

const Landing: React.FC = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const expired = searchParams.get('expired') === '1';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(
    expired ? { type: 'info', text: 'Your session ended. Enter your email to get a new sign-in link.' } : null
  );

  if (authService.isAuthenticated()) {
    return <Navigate to={redirect && redirect.startsWith('/') ? redirect : '/dashboard'} replace />;
  }

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await authService.sendMagicLink(email, redirect);
      setMessage({ type: 'success', text: `Sign-in link sent to ${email}. Open it on this device to continue.` });
      setEmail('');
    } catch (error: any) {
      setMessage({ type: 'error', text: errorMessage(error, 'Failed to send the link. Please try again.') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 10 }, flex: 1 }}>
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography variant="h1" sx={{ fontSize: { xs: '3.2rem', md: '4.5rem' }, color: 'primary.main', lineHeight: 1 }}>
            Secret Santa
          </Typography>
          <Typography variant="h6" component="p" sx={{ mt: 1, fontWeight: 400, color: 'text.secondary' }}>
            Draw names for the family, {new Date().getFullYear()}.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ mb: 1, fontFamily: 'inherit' }}>Sign in</Typography>
                <Typography sx={{ mb: 3, color: 'text.secondary' }}>
                  We'll email you a link. It signs you in on this device for 30 days.
                </Typography>

                {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

                <form onSubmit={handleSendMagicLink}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                    autoFocus
                    autoComplete="email"
                    inputProps={{ inputMode: 'email' }}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={loading}>
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Email me a sign-in link'}
                  </Button>
                </form>
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Use the email your organizer added you with.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ mb: 2, fontFamily: 'inherit' }}>How it works</Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, color: 'text.secondary', '& li': { mb: 1.5 } }}>
                  {NOTES.map((note) => <li key={note}>{note}</li>)}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <SiteFooter />
    </Box>
  );
};

export default Landing;
